import { createSelector } from 'reselect';
import { equals } from "./filterUtils";

import {
  getSelectedSearchOptionKey,
  isFilterValueSelected,
  getFilterValue,
  isFilterSelected,
  getSortByValue,
  getFilterLookup as getQueryLookup,
} from "../search/selectors";

import {
  getFilterCountLookup,
} from "../results/selectors";

import {
  getSorting,
  hasSorting,
} from "../content/selectors";


import { selectionTypes } from "./selectionTypes";
import { has, toObject } from "../../utils/objects";

const getFilterData = state => state.filters.data;

export const getAllFilters = createSelector(getFilterData,
  data => data.allFilters,
);

export const isInitialized = createSelector(
  getFilterData,
  data => Object.keys(data).length > 0,
);

export const getFilterKeys = createSelector(
  getAllFilters,
  allFilters => allFilters.map(filter => filter.key),
);

export const getHiddenFilterKeys = createSelector(
  getAllFilters,
  allFilters => allFilters
    .filter(filter => filter.isHidden)
    .map(filter => filter.key),
);

const getFilterLookup = state => getAllFilters(state).reduce((map, filter) => {
    map[filter.key] = filter;

    if(filter.selectionType === selectionTypes.range){
      filter.options.forEach(rangeFilter => {
        map[rangeFilter.key] = rangeFilter;
      });
    }

    return map;
}, {});

const getFilter = state => filterKey => getFilterLookup(state)[filterKey];

export const getFilterOptions = state => filterKey => {
  const filter = getFilter(state)(filterKey);
  return filter ? filter.options : [];
};

const getFilters = filterKeys => state => {
  const lookup = getFilterLookup(state);
  return filterKeys.map(filter => lookup[filter]);
}

const _getPrimaryFilters = state => {
  const filterKeys = getSelectedSearchOption(state).primaryFilters;
  return getFilters(filterKeys)(state);
}

const getRangeFilter = (filter, state) => ({
  ...filter,
  options: filter.options.map(childFilter => ({
    ...childFilter,
    selectedValue: getFilterValue(state)(childFilter.key),
  })),
});

const getPrimaryListFilter = (filter, state, filterCountLookup, isDisabled) => {
  const options = isDisabled ? [] : filter.options.filter(option => {
    const lookupKey = `${filter.key}_${option.value}`;
    return has(filterCountLookup, lookupKey);
  });

  return {
    ...filter,
    options,
    selectedValue: getFilterValue(state)(filter.key),
    isDisabled: !options.length,
  };
};

const getInputFilter = (filter, state) => ({
  ...filter,
  options: filter.options.map(childFilter => ({
    ...childFilter,
    selectedValue: getFilterValue(state)(childFilter.key),
  })),
});

const isVisible = filter => !filter.isHidden;

export const getPrimaryFilters = state => {
  const filterCountLookup = getFilterCountLookup(state);
  const _isFilterSelected = isFilterSelected(state);

  return _getPrimaryFilters(state)
    .filter(isVisible)
    .map(filter => {
      const isDisabled = filter.dependsOn.some(parentKey => !_isFilterSelected(parentKey));
      const mapper = {
        [selectionTypes.list]: getPrimaryListFilter(filter, state, filterCountLookup, isDisabled),
        [selectionTypes.range]: getRangeFilter(filter, state),
        [selectionTypes.input]: getInputFilter(filter, state),
      };

      if(!has(mapper, filter.selectionType))
        throw new Error("Invalid selectionType on filter " + filter.key);

      return mapper[filter.selectionType];
    },
  );
};

const getFiltersContent = createSelector(
  getFilterData,
  data => data.content || {},
);

export const getPrimaryFilterLabel = createSelector(
  getFiltersContent,
  content => content.primaryFilterLabel,
);

export const getResetButtonText = createSelector(
  getFiltersContent,
  content => content.resetButtonText,
);

const mapOption = (state, filterCountLookup) => (parentKey, option, alwaysEnabled = false) => {
  const lookupKey = `${parentKey}_${option.value}`;
  const count = has(filterCountLookup, lookupKey) ? filterCountLookup[lookupKey] : null;
  const checked = isFilterValueSelected(state)(parentKey, option.value);
  const disabled = alwaysEnabled ? false : !count && !checked;

  return {
    key: lookupKey,
    label: option.label,
    value: option.value,
    checked,
    count,
    disabled,
    tooltipText: option.tooltipText,
  };
};

const getListFilter = (filter, _mapOption) => ({
  ...filter,
  options: filter.options.map(option => _mapOption(filter.key, option, false)),
});

const getStepFilter = (filter, _getFilterValue) => ({
  ...filter,
  valueFormat: filter.valueFormat || "{value}",
  options: filter.options.map(option => ({
    min: option.min,
    max: option.max,
    step: option.step,
    value: _getFilterValue(filter.key) || option.defaultValue,
  })),
});

const getSingleFilter = (filter, _getFilterValue, _mapOption) => ({
  ...filter,
  valueFormat: _getFilterValue(filter.key)
    ? filter.selectedLabel
    : filter.unselectedLabel,
  options: filter.options.map(option => _mapOption(filter.key, option, true)),
});

const _getSecondaryFilters = state => {
  const filterKeys = getSelectedSearchOption(state).secondaryFilters;
  return getFilters(filterKeys)(state);
}

export const getSecondaryFilters = state => {
  const _getFilterValue = getFilterValue(state);
  const _isFilterSelected = isFilterSelected(state);

  const filterCountLookup = getFilterCountLookup(state);
  const _mapOption = mapOption(state, filterCountLookup, false);

  return _getSecondaryFilters(state)
    .filter(isVisible)
    .map(filter => {
      const f = {
        ...filter,
        canReset: filter.allowReset && _isFilterSelected(filter.key),
      };

      if (filter.selectionType === selectionTypes.list)
        return getListFilter(f, _mapOption);
      else if (filter.selectionType === selectionTypes.step || filter.selectionType === selectionTypes.input)
        return getStepFilter(f, _getFilterValue);
      else if (filter.selectionType === selectionTypes.single)
        return getSingleFilter(f, _getFilterValue, _mapOption);
      else
        return f;
    },
  );
}

export const getSecondaryFilterLabel = createSelector(
  getFilterData,
  data => data.content.secondaryFilterLabel,
);

export const getPrimaryFilterToggleText = createSelector(
  getFiltersContent,
  content => content.primaryFilterToggleText,
);

export const getPrimaryFilterCTA = createSelector(
  getFiltersContent,
  content => content.primaryFilterCTA,
);

export const getSecondaryFilterToggleText = createSelector(
  getFiltersContent,
  content => content.secondaryFilterToggleText,
);

export const getSecondaryFilterCTA = createSelector(
  getFiltersContent,
  content => content.secondaryFilterCTA,
);

const getSearchOptionsData = state => {
  const data = getFilterData(state);
  return data.searchOptions
};

export const getDefaultSearchOptionKey = createSelector(
  getSearchOptionsData,
  getFilterData,
  (searchOptions, data) => data.defaultSearchOption || searchOptions[0].key,
);

export const getSelectedSearchOption = state => {
  const searchOptions = getSearchOptionsData(state);
  return searchOptions.find(option => option.key === getSelectedSearchOptionKey(state)) || searchOptions[0];
}

export const getSearchOptions = createSelector(
  getSearchOptionsData,
  getSelectedSearchOption,
  (options, selectedOption) => ({
    label: selectedOption.label,
    filterKey: 'searchBy',
    selectedValue: selectedOption.key,
    options: options.map(option => ({
      label: option.label,
      value: option.key,
    })),
  }),
);

export const getSearchOptionLabel = createSelector(
  getSelectedSearchOption,
  selectedSearchOption => selectedSearchOption.label,
);

export const canSortResults = createSelector(
  hasSorting,
  value => !!value,
);

export const getDefaultSortOption = createSelector(
  getSorting,
  sorting => sorting.value,
);

export const getSortOptions = createSelector(
  getSorting,
  getSortByValue,
  (sorting, sortBy) => ({
    filterKey: sorting.key,
    label: sorting.label,
    options: sorting.options,
    selectedValue: sortBy || sorting.value,
  }),
);

export const getDependentFilters = createSelector(
  getAllFilters,
  allFilters => {
    const getDependents = key => allFilters
      .filter(f => f.dependsOn.includes(key))
      .map(f => f.key);

    const entries = allFilters
      .map(filter => [filter.key, getDependents(filter.key)])
      .filter(([_, value]) => value.length);

    return toObject(entries);
  },
);

export const getFilterLabels = createSelector(
  getQueryLookup,
  state => state,
  (filterLookup, state) => {
    const getOptions = getFilterOptions(state);
    const entries = Object.entries(filterLookup).map(([key, values]) => {
      const option = getOptions(key).find(o => equals(o.value, values[0]));
      if (option) {
        return [key, { label: option.label, value: values[0]}]
      }
      return null;
    }).filter(entry => entry !== null);
    return toObject(entries);
  },
);